import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import {
    HttpContentWithData,
    HttpResponse,
    RQAPICall,
    RQAPICallWithData,
} from 'store/APIClient';
import { SIMULATION_ENDPOINT_PREFIX } from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import { dispatch } from 'store/store';
import {
    FreeHypothesis,
    FreeHypothesisEditMutationParams,
    FreeHypothesisMutationParams,
    OperationType,
    ValueType,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/types';
import { camelizeKeys, processKeys, snakify } from 'helpers/syntaxHelper';
import { useHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/contexts/HypothesesChangesContext';
import { filterFilters } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/dataHelpers';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import { useRef } from 'react';
import { t } from 'i18next';

const FREE_HYPOTHESES_API_KEY = 'free_hypotheses';

export const useGetHrFreeHypotheses = (
    hrSimulationId: number,
    enabled: boolean = true,
): UseQueryResult<FreeHypothesis[]> => {
    const endpoint = `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/free_hypotheses`;

    return useQuery({
        queryKey: [FREE_HYPOTHESES_API_KEY, hrSimulationId],
        queryFn: async () => {
            const getResponse = await RQAPICall({
                endpoint,
                method: 'GET',
            });

            return getResponse.content.data;
        },
        enabled,
        meta: {
            useGlobalOnError: true,
        },
    });
};
export const useFreeHypothesisPostMutation = ({
    hrSimulationId,
}: {
    hrSimulationId: number;
}) => {
    const endpoint = `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/free_hypotheses`;
    const { setHasChanges } = useHypothesesChangesContext();
    const queryClient = useQueryClient();
    const lastPayload = useRef<FreeHypothesisMutationParams | null>(null);
    return useMutation({
        mutationFn: async ({
            operation,
            valueType,
            newValue,
            filters,
            agentsNumber,
        }: FreeHypothesisMutationParams) => {
            if (hrSimulationId === undefined) {
                return {} as Promise<
                    HttpResponse<HttpContentWithData<FreeHypothesis>>
                >;
            }
            // ensure that for a fix amount, the value type is 'amount' and not 'percent'
            // TODO : review this logic when a new hypothesis type is created
            lastPayload.current = {
                operation,
                valueType,
                newValue,
                filters,
                agentsNumber,
            };
            return RQAPICallWithData<FreeHypothesis>({
                endpoint,
                method: 'POST',
                body: {
                    operation,
                    valueType:
                        operation === OperationType.FIX ? null : valueType,
                    value: newValue,
                    filters: filterFilters(filters),
                },
                preProcessingBodyCallback: (body) => processKeys(snakify, body),
                skipCaseConversion: true,
            });
        },

        onSuccess: ({ content }) => {
            const camelizedData = camelizeKeys(
                content.data,
            ) as unknown as FreeHypothesis;
            const existingData = queryClient.getQueryData<FreeHypothesis[]>([
                FREE_HYPOTHESES_API_KEY,
                hrSimulationId,
            ]);

            if (existingData) {
                existingData.push(camelizedData);
                queryClient.setQueryData(
                    [FREE_HYPOTHESES_API_KEY, hrSimulationId],
                    existingData,
                );
            }
            trackEventPRH(
                TRACKABLE_EVENTS.human_resources.simulation.scenario
                    .createIfseHypotheses,
                {
                    hypothesisType: 'IFSE',
                    operation: t(
                        `humanResources.simulation.freeHypothesis.ifse.${lastPayload.current?.operation}`,
                    ),
                    valueType:
                        lastPayload.current?.valueType === ValueType.PERCENT
                            ? '%'
                            : '€',
                    value: lastPayload.current?.newValue,
                },
            );
            setHasChanges(true);
            dispatch.vision.notifyUser({
                message: content.message,
                snackType: 'success',
            });
        },
        meta: { useGlobalOnError: true },
    });
};
const useFreeHypothesisEditMutation = ({
    onSuccess,
    hrSimulationId,
}: {
    onSuccess: () => void;
    hrSimulationId: number;
}) => {
    const queryClient = useQueryClient();
    const hypothesisId = useRef<number>(0);

    return useMutation({
        mutationFn: async ({
            id,
            filters,
            operation,
            newValue,
            valueType,
        }: FreeHypothesisEditMutationParams) => {
            if (id === undefined) return undefined;
            hypothesisId.current = id;
            const filteredFilters = filterFilters(filters);
            // ensure that for a fix amount, the value type is 'amount' and not 'percent'
            // TODO : review this logic when a new hypothesis type is created
            return RQAPICallWithData<FreeHypothesis>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/free_hypothesis/${id}`,
                method: 'PUT',
                body: {
                    valueType:
                        operation === OperationType.FIX ? null : valueType,
                    value: newValue,
                    operation,
                    filters: filteredFilters,
                },
                preProcessingBodyCallback: (body) => processKeys(snakify, body),
                skipCaseConversion: true,
            });
        },

        onSuccess: (response) => {
            onSuccess();

            const updatedFreeHypothesis = response?.content.data;
            queryClient.setQueryData(
                [FREE_HYPOTHESES_API_KEY, hrSimulationId],
                (oldData: FreeHypothesis[]) =>
                    oldData.map((hypothesis) =>
                        hypothesis.id === hypothesisId.current
                            ? updatedFreeHypothesis
                            : hypothesis,
                    ),
            );
            queryClient.invalidateQueries({
                queryKey: [FREE_HYPOTHESES_API_KEY, hrSimulationId],
            });

            dispatch.vision.notifyUser({
                message: response?.content.message ?? '',
                snackType: 'success',
            });
        },
        meta: { useGlobalOnError: true },
    });
};

const useFreeHypothesisDeleteMutation = ({
    onSuccess,
    hypothesisId,
    hrSimulationId,
}: {
    onSuccess: () => void;
    hypothesisId?: number;
    hrSimulationId: number;
}) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () =>
            RQAPICallWithData({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/free_hypothesis/${hypothesisId}`,
                method: 'DELETE',
            }),

        onSuccess: (response) => {
            queryClient.setQueryData(
                [FREE_HYPOTHESES_API_KEY, hrSimulationId],
                (oldData: FreeHypothesis[]) =>
                    oldData.filter(
                        (hypothesis) => hypothesis.id !== hypothesisId,
                    ),
            );
            queryClient.invalidateQueries({
                queryKey: [FREE_HYPOTHESES_API_KEY, hrSimulationId],
            });
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });
            onSuccess();
        },
        meta: { useGlobalOnError: true },
    });
};

export const useFreeHypothesisCRUDMutations = ({
    hrSimulationId,
    hypothesisId,
}: {
    hrSimulationId: number;
    hypothesisId?: number;
}) => {
    const { setHasChanges } = useHypothesesChangesContext();

    const onSuccess = () => {
        setHasChanges(true);
    };

    const { mutate: createFreeHypothesis, isPending: createLoading } =
        useFreeHypothesisPostMutation({
            hrSimulationId,
        });
    const { mutate: editFreeHypothesis, isPending: editLoading } =
        useFreeHypothesisEditMutation({
            onSuccess,
            hrSimulationId,
        });
    const { mutate: deleteFreeHypothesis, isPending: deleteLoading } =
        useFreeHypothesisDeleteMutation({
            onSuccess,
            hypothesisId,
            hrSimulationId,
        });

    return {
        createFreeHypothesis,
        editFreeHypothesis,
        deleteFreeHypothesis,
        isLoading: createLoading || editLoading || deleteLoading,
    };
};
